import { isApiError, reportClientError } from '@instanexus/sdk';
import { Component, type ErrorInfo, type ReactNode } from 'react';

import { ErrorFallback } from '@/components/error-fallback';

export interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches a render-time throw anywhere below it (providers, shell) and shows `ErrorFallback`
 * instead of a white screen (CLAUDE.md #9). Throws inside a routed page never reach this —
 * React Router's `errorElement` (`RouteErrorFallback`) takes them first — so this covers
 * everything ABOVE the router. A class component: React still has no hook equivalent.
 * Recovery is a full reload, which works wherever the crash happened.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    reportClientError({
      source: 'render',
      message: 'Unhandled render error caught by ErrorBoundary',
      requestId: isApiError(error) ? error.requestId : undefined,
      cause: { error, componentStack: info.componentStack },
    });
  }

  private readonly handleReset = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (error !== null) {
      return (
        <ErrorFallback
          requestId={isApiError(error) ? error.requestId : undefined}
          onReset={this.handleReset}
        />
      );
    }
    return this.props.children;
  }
}
